
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Clock3,
  Boxes,
  ChevronDown,
  Edit3,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Store,
  UploadCloud,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import useCuisine from "../../services/Order/useCuisine";

const TYPE_FILTERS = ["ALL", "VEG", "OTHER"];

const PACKAGE_FILTERS = [
  "ALL",
  "CONFIGURABLE_PACKAGE",
  "FIXED_PACKAGE",
  "THALI",
  "BULK_PACKAGE",
  "COMBO",
];

const getPackageTypeLabel = (type) => {
  const value = String(type || "").toUpperCase();

  const labels = {
    CONFIGURABLE_PACKAGE: "Configurable",
    FIXED_PACKAGE: "Fixed Package",
    THALI: "Thali",
    BULK_PACKAGE: "Bulk Package",
    COMBO: "Combo",
  };

  return labels[value] || value || "Other";
};

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  return `₹${number.toFixed(2)}`;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("Image file missing"));
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");

      if (!result.startsWith("data:image/")) {
        reject(new Error("Invalid image data"));
        return;
      }

      // IMPORTANT:
      // Upload ORIGINAL selected image bytes.
      // No canvas conversion, no resize, no forced WEBP/PNG conversion.
      resolve(result);
    };

    reader.onerror = () => reject(new Error("Unable to read selected image"));

    reader.readAsDataURL(file);
  });

const validateImageFile = async (file) => {
  if (!file) {
    throw new Error("Image file missing");
  }

  if (!String(file.type || "").startsWith("image/")) {
    throw new Error("Please select a valid image");
  }

  // Confirm browser can actually decode the selected image.
  // We only validate it; we do NOT convert/re-encode it.
  let bitmap = null;

  try {
    bitmap = await createImageBitmap(file);

    if (!bitmap.width || !bitmap.height) {
      throw new Error("Invalid image dimensions");
    }

    return true;
  } catch (error) {
    throw new Error("Selected image is corrupt or unsupported");
  } finally {
    if (bitmap?.close) {
      bitmap.close();
    }
  }
};

const normalizeTax = (value) => {
  if (value === "" || value === null || value === undefined) return "0";
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : "0";
};

const getTypeLabel = (type) => {
  const value = String(type || "").toUpperCase();
  if (value === "NON_VEG") return "Non Veg";
  if (value === "VEG") return "Veg";
  return value || "Other";
};

function PackageVariantsPanel({
  packageId,
  packageName,
  variantsByPackage,
  loadingByPackage,
  saving,
  deletingId,
  fetchVariants,
  insertVariant,
  updateVariant,
  deleteVariant,
}) {
  const finalPackageId = String(packageId ?? "");
  const hasLoaded = Object.prototype.hasOwnProperty.call(
    variantsByPackage || {},
    finalPackageId,
  );
  const variants = Array.isArray(variantsByPackage?.[finalPackageId])
    ? variantsByPackage[finalPackageId]
    : [];
  const loading = Boolean(loadingByPackage?.[finalPackageId]);

  const saveLockRef = useRef(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [deleteVariantTarget, setDeleteVariantTarget] = useState(null);
  const [variantForm, setVariantForm] = useState({
    variantId: "",
    variantName: "",
    minimumPersons: "",
    maximumPersons: "",
    exactPersons: "",
    price: "",
    status: "ACTIVE",
  });

  useEffect(() => {
    if (!finalPackageId || hasLoaded) return;

    fetchVariants(finalPackageId);

    // Service layer already de-duplicates StrictMode/overlapping GET calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalPackageId, hasLoaded]);

  const openAdd = () => {
    setEditingVariant({ mode: "ADD" });
    setVariantForm({
      variantId: "",
      variantName: "",
      minimumPersons: "",
      maximumPersons: "",
      exactPersons: "",
      price: "",
      status: "ACTIVE",
    });
  };

  const openEdit = (variant) => {
    setEditingVariant({ mode: "EDIT", variant });
    setVariantForm({
      variantId: String(variant?.package_variant_id ?? ""),
      variantName: String(variant?.variant_name ?? ""),
      minimumPersons:
        variant?.minimum_persons === null ||
        variant?.minimum_persons === undefined
          ? ""
          : String(variant.minimum_persons),
      maximumPersons:
        variant?.maximum_persons === null ||
        variant?.maximum_persons === undefined
          ? ""
          : String(variant.maximum_persons),
      exactPersons:
        variant?.exact_persons === null || variant?.exact_persons === undefined
          ? ""
          : String(variant.exact_persons),
      price: String(variant?.price ?? ""),
      status: String(variant?.status ?? "ACTIVE"),
    });
  };

  const closeModal = () => {
    if (saving) return;
    setEditingVariant(null);
  };

  const handleField = (event) => {
    const { name, value } = event.target;
    setVariantForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (saveLockRef.current || saving) return;

    const variantName = variantForm.variantName.trim();

    if (!variantName) {
      toast.error("Variant name is required");
      return;
    }

    if (variantForm.price === "" || Number(variantForm.price) < 0) {
      toast.error("Please enter a valid price");
      return;
    }

    const min =
      variantForm.minimumPersons === ""
        ? null
        : Number(variantForm.minimumPersons);
    const max =
      variantForm.maximumPersons === ""
        ? null
        : Number(variantForm.maximumPersons);
    const exact =
      variantForm.exactPersons === "" ? null : Number(variantForm.exactPersons);

    if (exact !== null && exact <= 0) {
      toast.error("Exact persons must be greater than 0");
      return;
    }

    if (min !== null && min < 0) {
      toast.error("Minimum persons cannot be negative");
      return;
    }

    if (max !== null && max < 0) {
      toast.error("Maximum persons cannot be negative");
      return;
    }

    if (min !== null && max !== null && max < min) {
      toast.error("Maximum persons cannot be less than minimum persons");
      return;
    }

    saveLockRef.current = true;

    try {
      const payload = {
        packageId: finalPackageId,
        variantName,
        minimumPersons: variantForm.minimumPersons,
        maximumPersons: variantForm.maximumPersons,
        exactPersons: variantForm.exactPersons,
        price: variantForm.price,
        status: variantForm.status,
      };

      const success =
        editingVariant?.mode === "EDIT"
          ? await updateVariant({
              ...payload,
              variantId: variantForm.variantId,
            })
          : await insertVariant(payload);

      if (success) {
        setEditingVariant(null);
      }
    } finally {
      saveLockRef.current = false;
    }
  };

  const handleDelete = (variant) => {
    const variantId = String(variant?.package_variant_id ?? "");

    if (!variantId) return;

    setDeleteVariantTarget(variant);
  };

  const closeDeleteModal = () => {
    const targetId = String(deleteVariantTarget?.package_variant_id ?? "");
    const isDeletingTarget = targetId && String(deletingId) === targetId;

    if (isDeletingTarget) return;

    setDeleteVariantTarget(null);
  };

  const confirmDeleteVariant = async () => {
    const variantId = String(deleteVariantTarget?.package_variant_id ?? "");

    if (!variantId) return;

    const success = await deleteVariant({
      packageVariantId: variantId,
      packageId: finalPackageId,
    });

    if (success) {
      setDeleteVariantTarget(null);
    }
  };

  return (
    <>
      <div className="mt-2 rounded-lg border border-sky-100 bg-white p-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-[10px] font-semibold text-slate-700">
              Variants
            </p>

            {variants.length > 0 ? (
              <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-50 px-1 text-[8px] font-bold text-sky-700">
                {variants.length}
              </span>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => fetchVariants(finalPackageId)}
              disabled={loading || saving}
              title="Refresh variants"
              aria-label="Refresh variants"
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
            </button>

            <button
              type="button"
              onClick={openAdd}
              disabled={saving}
              title="Add variant"
              aria-label="Add variant"
              className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-sky-600 text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={11} />
            </button>
          </div>
        </div>

        {loading && !hasLoaded ? (
          <div className="mt-1.5 grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="h-[58px] animate-pulse rounded-lg bg-slate-100"
              />
            ))}
          </div>
        ) : variants.length > 0 ? (
          <div className="mt-1.5 grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
            {variants.map((variant) => {
              const variantId = String(variant?.package_variant_id ?? "");
              const isDeleting = String(deletingId) === variantId;
              const isActive =
                String(variant?.status || "").toUpperCase() === "ACTIVE";

              const personText = variant?.exact_persons
                ? `${variant.exact_persons} P`
                : variant?.minimum_persons || variant?.maximum_persons
                  ? `${variant?.minimum_persons || "—"}-${
                      variant?.maximum_persons || "∞"
                    }`
                  : "—";

              return (
                <div
                  key={variantId}
                  className="group min-w-0 rounded-lg border border-slate-200 bg-slate-50/70 p-1.5 transition hover:border-sky-200 hover:bg-white hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-1">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-1">
                        <p
                          className="truncate text-[9px] font-semibold leading-3 text-slate-700"
                          title={
                            variant?.variant_name || `Variant #${variantId}`
                          }
                        >
                          {variant?.variant_name || `#${variantId}`}
                        </p>

                        <span
                          className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                            isActive ? "bg-emerald-500" : "bg-rose-500"
                          }`}
                          title={variant?.status || "-"}
                        />
                      </div>

                      <p className="mt-0.5 truncate text-[8px] leading-3 text-slate-400">
                        #{variantId}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => openEdit(variant)}
                        disabled={saving || isDeleting}
                        title="Edit variant"
                        aria-label="Edit variant"
                        className="inline-flex h-5 w-5 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:opacity-50"
                      >
                        <Edit3 size={8} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(variant)}
                        disabled={saving || isDeleting}
                        title="Delete variant"
                        aria-label="Delete variant"
                        className="inline-flex h-5 w-5 items-center justify-center rounded border border-rose-100 bg-rose-50 text-rose-600 transition hover:bg-rose-100 disabled:opacity-50"
                      >
                        {isDeleting ? (
                          <Loader2 size={8} className="animate-spin" />
                        ) : (
                          <Trash2 size={8} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mt-1 flex items-center justify-between gap-1 text-[8px] leading-3">
                    <span
                      className="truncate font-bold text-slate-700"
                      title={formatPrice(variant?.price)}
                    >
                      {formatPrice(variant?.price)}
                    </span>
                    <span
                      className="shrink-0 rounded bg-white px-1 py-0.5 font-semibold text-slate-500"
                      title="Persons"
                    >
                      {personText}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {editingVariant ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px] sm:p-5">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-5">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editingVariant.mode === "EDIT"
                    ? "Edit Package Variant"
                    : "Add Package Variant"}
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {packageName || "Package"} · #{finalPackageId}
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-5">
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Variant Name
                </label>
                <input
                  name="variantName"
                  value={variantForm.variantName}
                  onChange={handleField}
                  disabled={saving}
                  placeholder="Example: 11 Persons"
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Exact Persons
                </label>
                <input
                  name="exactPersons"
                  type="number"
                  min="0"
                  value={variantForm.exactPersons}
                  onChange={handleField}
                  disabled={saving}
                  placeholder="Example: 11"
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Price
                </label>
                <input
                  name="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={variantForm.price}
                  onChange={handleField}
                  disabled={saving}
                  placeholder="0.00"
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Minimum Persons
                </label>
                <input
                  name="minimumPersons"
                  type="number"
                  min="0"
                  value={variantForm.minimumPersons}
                  onChange={handleField}
                  disabled={saving}
                  placeholder="Optional"
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Maximum Persons
                </label>
                <input
                  name="maximumPersons"
                  type="number"
                  min="0"
                  value={variantForm.maximumPersons}
                  onChange={handleField}
                  disabled={saving}
                  placeholder="Optional"
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Status
                </label>
                <select
                  name="status"
                  value={variantForm.status}
                  onChange={handleField}
                  disabled={saving}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50"
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 text-xs font-semibold text-white transition hover:bg-sky-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : editingVariant.mode === "EDIT" ? (
                  <Edit3 size={14} />
                ) : (
                  <Plus size={14} />
                )}
                {saving
                  ? "Saving..."
                  : editingVariant.mode === "EDIT"
                    ? "Update Variant"
                    : "Add Variant"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteVariantTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px] sm:p-5">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-4 py-4 sm:px-5">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                  <Trash2 size={19} />
                </div>

                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">
                    Delete Package Variant
                  </h3>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Review the details before permanently deleting this variant.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={
                  String(deletingId) ===
                  String(deleteVariantTarget?.package_variant_id ?? "")
                }
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close delete confirmation"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 sm:p-5">
              <div className="rounded-xl border border-rose-100 bg-rose-50/70 px-3 py-2.5">
                <p className="text-xs font-semibold text-rose-700">
                  This action cannot be undone
                </p>
                <p className="mt-1 text-[11px] leading-5 text-rose-600">
                  The variant will be permanently removed after confirmation.
                </p>
              </div>

              <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                <div className="grid grid-cols-2 border-b border-slate-100 bg-slate-50/80">
                  <div className="border-r border-slate-100 px-3 py-2.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Variant Name
                    </p>
                    <p className="mt-1 break-words text-xs font-semibold text-slate-800">
                      {deleteVariantTarget?.variant_name || "—"}
                    </p>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Status
                    </p>
                    <span
                      className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                        String(
                          deleteVariantTarget?.status || "",
                        ).toUpperCase() === "ACTIVE"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-rose-100 text-rose-700"
                      }`}
                    >
                      {deleteVariantTarget?.status || "—"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 border-b border-slate-100">
                  <div className="border-r border-slate-100 px-3 py-2.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Variant ID
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-800">
                      #{deleteVariantTarget?.package_variant_id || "—"}
                    </p>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Package ID
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-800">
                      #{deleteVariantTarget?.package_id || finalPackageId}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 border-b border-slate-100 bg-slate-50/50">
                  <div className="border-r border-slate-100 px-3 py-2.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Price
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-800">
                      {formatPrice(deleteVariantTarget?.price)}
                    </p>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Exact Persons
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-800">
                      {deleteVariantTarget?.exact_persons ?? "—"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2">
                  <div className="border-r border-slate-100 px-3 py-2.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Minimum Persons
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-800">
                      {deleteVariantTarget?.minimum_persons ?? "—"}
                    </p>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                      Maximum Persons
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-800">
                      {deleteVariantTarget?.maximum_persons ?? "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/70 px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={
                  String(deletingId) ===
                  String(deleteVariantTarget?.package_variant_id ?? "")
                }
                className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDeleteVariant}
                disabled={
                  String(deletingId) ===
                  String(deleteVariantTarget?.package_variant_id ?? "")
                }
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-rose-600 px-5 text-xs font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {String(deletingId) ===
                String(deleteVariantTarget?.package_variant_id ?? "") ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                {String(deletingId) ===
                String(deleteVariantTarget?.package_variant_id ?? "")
                  ? "Deleting..."
                  : "Delete Variant"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default function Cuisine() {
  const {
    userID,

    // Packages API
    bhandarePackages,
    bhandarePackagesLoading,
    bhandarePackageSaving,
    bhandarePackageTree,
    fetchBhandareWalaPackages,
    updateBhandareWalaPackage,

    // Package Variants API
    bhandarePackageVariants,
    bhandarePackageVariantsLoading,
    bhandarePackageVariantSaving,
    bhandarePackageVariantDeletingId,
    fetchBhandareWalaPackageVariants,
    insertBhandareWalaPackageVariant,
    updateBhandareWalaPackageVariant,
    deleteBhandareWalaPackageVariant,

    // Items API
    bhandareItems,
    bhandareItemsLoading,
    bhandareItemSaving,
    bhandareImageUploading,
    fetchBhandareWalaItems,
    uploadBhandareWalaImage,
    insertBhandareWalaItem,
    updateBhandareWalaItem,
  } = useCuisine();

  const fileInputRef = useRef(null);
  const packageFileInputRef = useRef(null);

  // Synchronous guards: state updates are async, so these block a second
  // click immediately before React has time to disable the Save button.
  const initialFetchStartedRef = useRef(false);
  const packageSaveLockRef = useRef(false);
  const itemSaveLockRef = useRef(false);

  const [activeTab, setActiveTab] = useState("PACKAGES");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [packageTypeFilter, setPackageTypeFilter] = useState("ALL");
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const [selectedComboDetails, setSelectedComboDetails] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const [editingPackage, setEditingPackage] = useState(null);
  const [packageSelectedFile, setPackageSelectedFile] = useState(null);
  const [packageImagePreview, setPackageImagePreview] = useState("");
  const [packageForm, setPackageForm] = useState({
    packageId: "",
    catalogueId: "",
    parentPackageId: "",
    packageName: "",
    packageType: "CONFIGURABLE_PACKAGE",
    description: "",
    pricingMethod: "SIZE_TIER",
    minimumPersons: "1",
    maximumPersons: "",
    personIncrement: "1",
    basePrice: "",
    displayOrder: "0",
    status: "ACTIVE",
    imgUrl: "",
  });
  const [form, setForm] = useState({
    item_id: "",
    restaurant_id: "1",
    item_name: "",
    item_description: "",
    item_type: "VEG",
    item_url: "",
    tax_percentage: "0",
    addon_price: "",
    less_price: "",
    upgrade_price: "",
    preparation_time_minutes: "",
    status: "ACTIVE",
  });

  const isAllowedUser =
    String(userID || "").toUpperCase() === "A1437" || "A1443";
  const isBusy = bhandareItemSaving || bhandareImageUploading;
  const isPackageBusy = bhandarePackageSaving || bhandareImageUploading;
  const isItemsSectionLoading = bhandareItemsLoading;
  const isCuisineLoading =
    activeTab === "PACKAGES" ? bhandarePackagesLoading : isItemsSectionLoading;

  const handleRefresh = async () => {
    if (activeTab === "PACKAGES") {
      await fetchBhandareWalaPackages();
      return;
    }

    await fetchBhandareWalaItems();
  };

  useEffect(() => {
    if (!isAllowedUser) return;

    // React 18 StrictMode runs mount effects twice in development.
    // This ref makes the initial API load execute only once.
    if (initialFetchStartedRef.current) return;
    initialFetchStartedRef.current = true;

    fetchBhandareWalaPackages();
    fetchBhandareWalaItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowedUser]);

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  useEffect(() => {
    return () => {
      if (packageImagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(packageImagePreview);
      }
    };
  }, [packageImagePreview]);

  const items = useMemo(
    () => (Array.isArray(bhandareItems) ? bhandareItems : []),
    [bhandareItems],
  );

  // Parent -> child package mapping is already prepared by UseCuisine.
  // Example:
  // Birthday Package -> Combo No. 1...20
  // Rice Combos -> its child packages
  const packageTree = useMemo(
    () => (Array.isArray(bhandarePackageTree) ? bhandarePackageTree : []),
    [bhandarePackageTree],
  );

  const packageCounts = useMemo(() => {
    return bhandarePackages.reduce(
      (acc, pkg) => {
        acc.ALL += 1;

        const key = String(pkg?.package_type || "").toUpperCase();

        if (Object.prototype.hasOwnProperty.call(acc, key)) {
          acc[key] += 1;
        }

        return acc;
      },
      {
        ALL: 0,
        CONFIGURABLE_PACKAGE: 0,
        FIXED_PACKAGE: 0,
        THALI: 0,
        BULK_PACKAGE: 0,
        COMBO: 0,
      },
    );
  }, [bhandarePackages]);

  const filteredPackageTree = useMemo(() => {
    const query = search.trim().toLowerCase();

    const matchesPackage = (pkg) => {
      const type = String(pkg?.package_type || "").toUpperCase();

      const matchesType =
        packageTypeFilter === "ALL" || type === packageTypeFilter;

      const matchesSearch =
        !query ||
        String(pkg?.package_name || "")
          .toLowerCase()
          .includes(query) ||
        String(pkg?.description || "")
          .toLowerCase()
          .includes(query) ||
        String(pkg?.package_id || "").includes(query) ||
        String(pkg?.pricing_method || "")
          .toLowerCase()
          .includes(query);

      return matchesType && matchesSearch;
    };

    return packageTree
      .map((root) => {
        const rootMatches = matchesPackage(root);

        const matchingChildren = (root.children || []).filter(matchesPackage);

        if (rootMatches) {
          return {
            ...root,
            children: root.children || [],
          };
        }

        if (matchingChildren.length > 0) {
          return {
            ...root,
            children: matchingChildren,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [packageTree, packageTypeFilter, search]);

  const counts = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc.ALL += 1;
        const key = String(item?.item_type || "").toUpperCase();
        if (key === "VEG") acc.VEG += 1;
        if (key === "OTHER") acc.OTHER += 1;
        return acc;
      },
      { ALL: 0, VEG: 0, OTHER: 0 },
    );
  }, [items]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();

    return items.filter((item) => {
      const itemType = String(item?.item_type || "").toUpperCase();
      const matchesType = typeFilter === "ALL" || itemType === typeFilter;

      if (!matchesType) return false;
      if (!query) return true;

      return [
        item?.item_name,
        item?.item_description,
        item?.item_id,
        item?.preparation_time_minutes,
        item?.addon_price,
        item?.less_price,
        item?.upgrade_price,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(query),
      );
    });
  }, [items, search, typeFilter]);

  useEffect(() => {
    if (activeTab !== "PACKAGES") return;

    const visibleIds = new Set();

    filteredPackageTree.forEach((pkg) => {
      visibleIds.add(String(pkg?.package_id));

      (pkg?.children || []).forEach((child) => {
        visibleIds.add(String(child?.package_id));
      });
    });

    if (
      expandedPackageId !== null &&
      !visibleIds.has(String(expandedPackageId))
    ) {
      setExpandedPackageId(null);
    }
  }, [activeTab, filteredPackageTree, expandedPackageId]);

  useEffect(() => {
    if (
      expandedItemId !== null &&
      !filteredItems.some(
        (item) => String(item?.item_id) === String(expandedItemId),
      )
    ) {
      setExpandedItemId(null);
    }
  }, [filteredItems, expandedItemId]);

  const toggleItem = (itemId) => {
    setExpandedItemId((current) =>
      String(current) === String(itemId) ? null : itemId,
    );
  };

  const togglePackage = (packageId) => {
    setExpandedPackageId((current) =>
      String(current) === String(packageId) ? null : packageId,
    );
    setSelectedComboDetails(null);
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setSearch("");
    setExpandedItemId(null);
    setExpandedPackageId(null);
    setSelectedComboDetails(null);

    if (tab === "PACKAGES") {
      await fetchBhandareWalaPackages();
      return;
    }

    if (tab === "ITEMS") {
      await fetchBhandareWalaItems();
    }
  };

  const openPackageEdit = (pkg) => {
    const nextImage = String(pkg?.img_url || "").trim();

    setEditingPackage(pkg);
    setPackageSelectedFile(null);
    setPackageImagePreview(nextImage);

    setPackageForm({
      packageId: String(pkg?.package_id ?? ""),
      catalogueId: String(pkg?.catalogue_id ?? ""),
      parentPackageId:
        pkg?.parent_package_id === null || pkg?.parent_package_id === undefined
          ? ""
          : String(pkg.parent_package_id),
      packageName: String(pkg?.package_name ?? ""),
      packageType: String(pkg?.package_type ?? "CONFIGURABLE_PACKAGE"),
      description: String(pkg?.description ?? ""),
      pricingMethod: String(pkg?.pricing_method ?? "SIZE_TIER"),
      minimumPersons: String(pkg?.minimum_persons ?? "1"),
      maximumPersons:
        pkg?.maximum_persons === null || pkg?.maximum_persons === undefined
          ? ""
          : String(pkg.maximum_persons),
      personIncrement: String(pkg?.person_increment ?? "1"),
      basePrice:
        pkg?.base_price === null || pkg?.base_price === undefined
          ? ""
          : String(pkg.base_price),
      displayOrder: String(pkg?.display_order ?? "0"),
      status: String(pkg?.status ?? "ACTIVE"),
      imgUrl: nextImage,
    });
  };

  const closePackageEdit = () => {
    if (isPackageBusy) return;

    if (packageImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(packageImagePreview);
    }

    setEditingPackage(null);
    setPackageSelectedFile(null);
    setPackageImagePreview("");
  };

  const handlePackageFieldChange = (event) => {
    const { name, value } = event.target;

    setPackageForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePackageFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Please select a valid image");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2 MB");
      return;
    }

    if (packageImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(packageImagePreview);
    }

    setPackageSelectedFile(file);
    setPackageImagePreview(URL.createObjectURL(file));
  };

  const handlePackageSave = async () => {
    if (packageSaveLockRef.current) return;

    packageSaveLockRef.current = true;

    try {
      const packageName = packageForm.packageName.trim();

      if (!packageName) {
        toast.error("Package name is required");
        return;
      }

      if (!packageForm.packageType) {
        toast.error("Package type is required");
        return;
      }

      if (!packageForm.pricingMethod) {
        toast.error("Pricing method is required");
        return;
      }

      let finalImageUrl = String(packageForm.imgUrl || "");

      if (packageSelectedFile) {
        try {
          await validateImageFile(packageSelectedFile);
          const imageData = await fileToDataUrl(packageSelectedFile);

          const uploadResult = await uploadBhandareWalaImage({
            item_name:
              packageName || `bhandare_package_${packageForm.packageId}`,
            image_data: imageData,

            // Backend expects only IMG in the type field.
            // image_data still contains the original MIME prefix.
            type: "IMG",
          });

          if (!uploadResult?.image_url) return;

          finalImageUrl = uploadResult.image_url;
        } catch (error) {
          console.error("Bhandare package image read/upload error:", error);

          toast.error(error?.message || "Unable to upload package image");

          return;
        }
      }

      const success = await updateBhandareWalaPackage({
        ...packageForm,
        packageName,
        imgUrl: finalImageUrl,
      });

      if (success) {
        closePackageEdit();
      }
    } finally {
      packageSaveLockRef.current = false;
    }
  };

  const openAddItem = () => {
    if (isBusy) return;

    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setEditingItem({ mode: "ADD" });
    setSelectedFile(null);
    setImagePreview("");

    setForm({
      item_id: "",
      restaurant_id: "1",
      item_name: "",
      item_description: "",
      item_type: "VEG",
      item_url: "",
      tax_percentage: "0",
      addon_price: "",
      less_price: "",
      upgrade_price: "",
      preparation_time_minutes: "",
      status: "ACTIVE",
    });
  };

  const openEdit = (item) => {
    const nextImage = String(item?.image_url || "").trim();

    setEditingItem({ mode: "EDIT", item });
    setSelectedFile(null);
    setImagePreview(nextImage);
    setForm({
      item_id: String(item?.item_id ?? ""),
      restaurant_id: String(item?.restaurant_id ?? "1"),
      item_name: String(item?.item_name ?? ""),
      item_description: String(item?.item_description ?? ""),
      item_type: String(item?.item_type ?? "VEG"),
      item_url: nextImage,
      tax_percentage: normalizeTax(item?.tax_percentage),
      addon_price:
        item?.addon_price === null || item?.addon_price === undefined
          ? ""
          : String(item.addon_price),
      less_price:
        item?.less_price === null || item?.less_price === undefined
          ? ""
          : String(item.less_price),
      upgrade_price:
        item?.upgrade_price === null || item?.upgrade_price === undefined
          ? ""
          : String(item.upgrade_price),
      preparation_time_minutes:
        item?.preparation_time_minutes === null ||
        item?.preparation_time_minutes === undefined
          ? ""
          : String(item.preparation_time_minutes),
      status: String(item?.status ?? "ACTIVE"),
    });
  };

  const closeEdit = () => {
    if (isBusy) return;

    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setEditingItem(null);
    setSelectedFile(null);
    setImagePreview("");
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Please select a valid image");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2 MB");
      return;
    }

    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setSelectedFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (itemSaveLockRef.current || isBusy) return;

    const itemName = form.item_name.trim();

    if (!itemName) {
      toast.error("Item name is required");
      return;
    }

    if (
      form.tax_percentage !== "" &&
      Number.isNaN(Number(form.tax_percentage))
    ) {
      toast.error("Tax percentage must be a number");
      return;
    }

    if (
      form.preparation_time_minutes !== "" &&
      (Number.isNaN(Number(form.preparation_time_minutes)) ||
        Number(form.preparation_time_minutes) < 0)
    ) {
      toast.error("Preparation time must be 0 or greater");
      return;
    }

    itemSaveLockRef.current = true;

    try {
      let finalImageUrl = String(form.item_url || "");

      if (selectedFile) {
        try {
          await validateImageFile(selectedFile);
          const imageData = await fileToDataUrl(selectedFile);

          const uploadResult = await uploadBhandareWalaImage({
            item_name:
              itemName ||
              (editingItem?.mode === "ADD"
                ? `bhandare_item_${Date.now()}`
                : `bhandare_item_${form.item_id}`),
            image_data: imageData,
            type: "IMG",
          });

          if (!uploadResult?.image_url) return;

          finalImageUrl = uploadResult.image_url;
        } catch (error) {
          console.error("Bhandare image read/upload error:", error);
          toast.error(error?.message || "Unable to upload image");
          return;
        }
      }

      const commonPayload = {
        restaurant_id: form.restaurant_id || "1",
        item_name: itemName,
        item_description: form.item_description,
        item_type: form.item_type,
        tax_percentage: normalizeTax(form.tax_percentage),
        addon_price: form.addon_price,
        less_price: form.less_price,
        upgrade_price: form.upgrade_price,
        preparation_time_minutes: String(form.preparation_time_minutes || "0"),
        status: form.status || "ACTIVE",
      };

      const success =
        editingItem?.mode === "ADD"
          ? await insertBhandareWalaItem({
              ...commonPayload,
              image_url: finalImageUrl,
            })
          : await updateBhandareWalaItem({
              ...commonPayload,
              itemId: form.item_id,
              image_url: finalImageUrl,
            });

      if (success) {
        closeEdit();
      }
    } finally {
      itemSaveLockRef.current = false;
    }
  };

  if (!isAllowedUser) {
    return (
      <main className="h-[calc(100dvh-80px)] min-h-0 w-full overflow-y-auto p-6">
        <div className="flex min-h-full w-full items-center justify-center">
          <section className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white px-8 py-8 shadow-sm sm:px-10 sm:py-9">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <Clock3 size={26} strokeWidth={2} />
              </div>

              <h1 className="mt-6 text-2xl font-semibold tracking-tight text-slate-900">
                Cuisine — Coming Soon
              </h1>

              <p className="mt-2 text-sm text-slate-600">
                This module is currently under development.
              </p>

              <p className="mt-2 text-sm font-medium text-slate-800">
                We&apos;ll make it live soon 🚀
              </p>
            </div>

            <div className="my-7 h-px w-full bg-slate-200" />

            <div className="space-y-4 text-sm text-slate-700 sm:text-[15px]">
              <div className="flex items-center gap-3">
                <span className="shrink-0 text-lg font-semibold leading-none text-blue-600">
                  ✓
                </span>
                <span>Dynamic route-based page rendering</span>
              </div>

              <div className="flex items-center gap-3">
                <span className="shrink-0 text-lg font-semibold leading-none text-blue-600">
                  ✓
                </span>
                <span>Future feature placeholder</span>
              </div>

              <div className="flex items-center gap-3">
                <span className="shrink-0 text-lg font-semibold leading-none text-blue-600">
                  ✓
                </span>
                <span>Easy integration with new modules</span>
              </div>
            </div>

            <div className="mt-7 flex justify-center">
              <span className="inline-flex items-center rounded-full bg-amber-100 px-4 py-1.5 text-xs font-medium text-amber-800">
                🚧&nbsp; Work in Progress
              </span>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="h-[calc(100dvh-80px)] min-h-0 w-full overflow-hidden p-6">
        <section className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-2xl bg-white shadow-sm">
          <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 sm:px-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <Store size={18} />
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-slate-900">
                      Cuisine
                    </h2>

                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                      {bhandareItems.length} items
                    </span>

                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                      {bhandarePackages.length} packages
                    </span>
                  </div>

                  <p className="mt-0.5 text-xs text-slate-500">
                    Manage items and browse package hierarchy.
                  </p>
                </div>
              </div>

              <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                <div className="relative min-w-0 flex-1 sm:min-w-[280px] lg:w-[340px]">
                  <Search
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={
                      activeTab === "ITEMS"
                        ? "Search items..."
                        : "Search packages..."
                    }
                    className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-800 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                  />
                  {search ? (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      <X size={14} />
                    </button>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isCuisineLoading || isBusy}
                  className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 text-xs font-semibold text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw
                    size={14}
                    className={isCuisineLoading ? "animate-spin" : ""}
                  />
                  Refresh
                </button>
              </div>
            </div>

            <div className="mt-3 flex flex-col gap-3">
              <div className="flex items-end justify-between gap-3 border-b border-slate-200">
                <div className="flex min-w-0 items-center gap-6 overflow-x-auto">
                  <button
                    type="button"
                    onClick={() => handleTabChange("PACKAGES")}
                    className={`relative inline-flex h-10 shrink-0 items-center gap-2 border-b-2 px-0.5 text-sm font-medium transition ${
                      activeTab === "PACKAGES"
                        ? "border-emerald-500 text-emerald-600"
                        : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <Boxes size={15} />
                    <span>Packages</span>

                    <span
                      className={`text-[10px] font-semibold ${
                        activeTab === "PACKAGES"
                          ? "text-emerald-600"
                          : "text-slate-400"
                      }`}
                    >
                      {bhandarePackages.length}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTabChange("ITEMS")}
                    className={`relative inline-flex h-10 shrink-0 items-center gap-2 border-b-2 px-0.5 text-sm font-medium transition ${
                      activeTab === "ITEMS"
                        ? "border-emerald-500 text-emerald-600"
                        : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <Store size={15} />
                    <span>Items</span>

                    <span
                      className={`text-[10px] font-semibold ${
                        activeTab === "ITEMS"
                          ? "text-emerald-600"
                          : "text-slate-400"
                      }`}
                    >
                      {bhandareItems.length}
                    </span>
                  </button>
                </div>

                {activeTab === "ITEMS" ? (
                  <button
                    type="button"
                    onClick={openAddItem}
                    disabled={isBusy}
                    className="mb-1.5 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Plus size={13} />
                    Add Item
                  </button>
                ) : null}
              </div>

              {activeTab === "ITEMS" ? (
                <div className="flex flex-wrap items-center gap-2">
                  {TYPE_FILTERS.map((key) => {
                    const active = typeFilter === key;
                    const label =
                      key === "ALL" ? "All" : key === "VEG" ? "Veg" : "Other";

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setTypeFilter(key)}
                        className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition ${
                          active
                            ? "border-emerald-500 bg-emerald-500 text-white"
                            : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200 hover:bg-emerald-50"
                        }`}
                      >
                        {label}
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] ${
                            active
                              ? "bg-white/20 text-white"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {counts[key] || 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {PACKAGE_FILTERS.map((key) => {
                    const active = packageTypeFilter === key;
                    const label =
                      key === "ALL" ? "All" : getPackageTypeLabel(key);

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setPackageTypeFilter(key)}
                        className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition ${
                          active
                            ? "border-blue-500 bg-blue-500 text-white"
                            : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50"
                        }`}
                      >
                        {label}
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] ${
                            active
                              ? "bg-white/20 text-white"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {packageCounts[key] || 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain bg-slate-50/60 p-3 [scrollbar-gutter:stable] sm:p-4">
            {activeTab === "ITEMS" ? (
              <>
                {bhandareItemsLoading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className="h-[68px] animate-pulse rounded-xl border border-slate-200 bg-white"
                    />
                  ))
                ) : filteredItems.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-14 text-center">
                    <p className="text-sm font-semibold text-slate-800">
                      No items found
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Try another search or filter.
                    </p>
                  </div>
                ) : (
                  filteredItems.map((item) => {
                    const itemId = item?.item_id;
                    const isExpanded =
                      String(expandedItemId) === String(itemId);
                    const imageUrl = String(item?.image_url || "").trim();
                    const isActive =
                      String(item?.status || "").toUpperCase() === "ACTIVE";
                    const type = String(item?.item_type || "")
                      .trim()
                      .toUpperCase()
                      .replace(/[\s-]+/g, "_");

                    const isVeg = type === "VEG";
                    const isNonVeg = type === "NON_VEG";
                    const compactItemDetails = [
                      { label: "Item ID", value: itemId ? `#${itemId}` : "—" },
                      { label: "Type", value: getTypeLabel(item?.item_type) },
                      {
                        label: "Tax",
                        value: `${Number(item?.tax_percentage || 0).toFixed(2)}%`,
                      },
                      {
                        label: "Preparation",
                        value: `${item?.preparation_time_minutes || "0"} min`,
                      },
                      { label: "Add Price", value: item?.addon_price ?? "—" },
                      { label: "Less Price", value: item?.less_price ?? "—" },
                      {
                        label: "Upgrade Price",
                        value: item?.upgrade_price ?? "—",
                      },
                      { label: "Status", value: item?.status || "—" },
                      { label: "Created", value: item?.created_at || "—" },
                      { label: "Updated", value: item?.updated_at || "—" },
                      // { label: "Image URL", value: item?.image_url || "—" },
                      {
                        label: "Description",
                        value:
                          item?.item_description || "No description added.",
                      },
                    ];
                    return (
                      <article
                        key={itemId}
                        className={`overflow-hidden rounded-xl border bg-white transition-all ${
                          isExpanded
                            ? "border-emerald-200 shadow-sm"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex min-h-[66px] items-center gap-3 px-3 py-2.5 sm:px-4">
                          <button
                            type="button"
                            onClick={() => toggleItem(itemId)}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          >
                            <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={item?.item_name || "Item"}
                                  loading="lazy"
                                  decoding="async"
                                  className="h-full w-full object-cover"
                                  onError={(event) => {
                                    event.currentTarget.style.display = "none";
                                    const fallback =
                                      event.currentTarget.nextElementSibling;
                                    if (fallback)
                                      fallback.style.display = "flex";
                                  }}
                                />
                              ) : null}

                              <div
                                style={{ display: imageUrl ? "none" : "flex" }}
                                className={`absolute inset-0 items-center justify-center ${
                                  isVeg
                                    ? "bg-emerald-50 text-emerald-600"
                                    : "bg-slate-50 text-slate-500"
                                }`}
                              >
                                <span className="h-2.5 w-2.5 rounded-full border border-current">
                                  <span className="m-auto mt-[2px] block h-1 w-1 rounded-full bg-current" />
                                </span>
                              </div>
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="truncate text-sm font-semibold text-slate-900 sm:text-[15px]">
                                  {item?.item_name || "Unnamed item"}
                                </h3>

                                <span
                                  className={`rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${
                                    isVeg
                                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                      : isNonVeg
                                        ? "border-red-200 bg-red-50 text-red-700"
                                        : "border-slate-300 bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {getTypeLabel(item?.item_type)}
                                </span>
                              </div>

                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
                                <span>
                                  Tax{" "}
                                  {Number(item?.tax_percentage || 0).toFixed(2)}
                                  %
                                </span>
                                <span>
                                  Prep {item?.preparation_time_minutes || "0"}{" "}
                                  min
                                </span>
                                {item?.addon_price !== null &&
                                item?.addon_price !== undefined &&
                                item?.addon_price !== "" ? (
                                  <span className="font-medium text-emerald-600">
                                    Add {String(item.addon_price)}
                                  </span>
                                ) : null}
                                {item?.less_price !== null &&
                                item?.less_price !== undefined &&
                                item?.less_price !== "" ? (
                                  <span className="font-medium text-rose-500">
                                    Less {String(item.less_price)}
                                  </span>
                                ) : null}
                                {item?.upgrade_price !== null &&
                                item?.upgrade_price !== undefined &&
                                item?.upgrade_price !== "" ? (
                                  <span className="font-medium text-blue-500">
                                    Upgrade {String(item.upgrade_price)}
                                  </span>
                                ) : null}
                                {item?.item_description ? (
                                  <span className="hidden max-w-[420px] truncate sm:inline">
                                    {item.item_description}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </button>

                          <div className="flex shrink-0 items-center gap-2">
                            <span
                              className={`hidden rounded-full px-2 py-1 text-[10px] font-semibold sm:inline-flex ${
                                isActive
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-rose-50 text-rose-700"
                              }`}
                            >
                              {item?.status || "-"}
                            </span>

                            <button
                              type="button"
                              onClick={() => openEdit(item)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600"
                              title="Edit item"
                            >
                              <Edit3 size={14} />
                            </button>

                            <button
                              type="button"
                              onClick={() => toggleItem(itemId)}
                              className={`flex h-8 w-8 items-center justify-center rounded-full transition ${
                                isExpanded
                                  ? "bg-emerald-500 text-white"
                                  : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                              }`}
                              title={
                                isExpanded ? "Hide details" : "View details"
                              }
                            >
                              {isExpanded ? (
                                <EyeOff size={14} />
                              ) : (
                                <Eye size={14} />
                              )}
                            </button>

                            <ChevronDown
                              size={16}
                              className={`hidden text-slate-400 transition-transform sm:block ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </div>

                        {isExpanded ? (
                          <div className="border-t border-slate-200 bg-slate-50/50 p-3 sm:p-4">
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[150px_minmax(0,1fr)_auto] lg:items-start">
                              <div className="relative h-[130px] w-full overflow-hidden rounded-xl border border-slate-200 bg-white sm:h-[145px] lg:w-[150px]">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={item?.item_name || "Item"}
                                    loading="lazy"
                                    decoding="async"
                                    className="h-full w-full object-cover"
                                    onError={(event) => {
                                      event.currentTarget.style.display =
                                        "none";
                                      const fallback =
                                        event.currentTarget.nextElementSibling;
                                      if (fallback)
                                        fallback.style.display = "flex";
                                    }}
                                  />
                                ) : null}

                                <div
                                  style={{
                                    display: imageUrl ? "none" : "flex",
                                  }}
                                  className="absolute inset-0 flex-col items-center justify-center gap-2 text-slate-300"
                                >
                                  <ImageIcon size={28} />
                                  <span className="text-[10px] font-medium">
                                    No image
                                  </span>
                                </div>
                              </div>

                              <div className="min-w-0">
                                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
                                  {compactItemDetails.map((detail) => (
                                    <div
                                      key={detail.label}
                                      className="min-w-0 rounded-lg border border-slate-200 bg-white px-2 py-1.5"
                                      title={String(detail.value ?? "—")}
                                    >
                                      <p className="truncate text-[8px] font-semibold uppercase tracking-wide text-slate-400">
                                        {detail.label}
                                      </p>
                                      <p className="mt-0.5 line-clamp-2 break-words text-[10px] font-semibold leading-4 text-slate-700">
                                        {String(detail.value ?? "—")}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => openEdit(item)}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 text-xs font-semibold text-white transition hover:bg-emerald-600 lg:mt-0"
                              >
                                <Edit3 size={14} />
                                Edit Item
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </article>
                    );
                  })
                )}
              </>
            ) : (
              <>
                {bhandarePackagesLoading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className="h-[72px] animate-pulse rounded-xl border border-slate-200 bg-white"
                    />
                  ))
                ) : filteredPackageTree.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-14 text-center">
                    <Boxes size={28} className="mx-auto text-slate-300" />
                    <p className="mt-3 text-sm font-semibold text-slate-800">
                      No packages found
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Try another search or package type.
                    </p>
                  </div>
                ) : (
                  filteredPackageTree.map((pkg) => {
                    const packageId = pkg?.package_id;
                    const isExpanded =
                      String(expandedPackageId) === String(packageId);
                    const imageUrl = String(pkg?.img_url || "").trim();
                    const isActive =
                      String(pkg?.status || "").toUpperCase() === "ACTIVE";
                    const childCount = Array.isArray(pkg?.children)
                      ? pkg.children.length
                      : 0;

                    return (
                      <article
                        key={packageId}
                        className={`overflow-hidden rounded-xl border bg-white transition-all ${
                          isExpanded
                            ? "border-blue-200 shadow-sm"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex min-h-[70px] items-center gap-3 px-3 py-2.5 sm:px-4">
                          <button
                            type="button"
                            onClick={() => togglePackage(packageId)}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          >
                            <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={pkg?.package_name || "Package"}
                                  loading="lazy"
                                  decoding="async"
                                  className="h-full w-full object-cover"
                                  onError={(event) => {
                                    event.currentTarget.style.display = "none";
                                    const fallback =
                                      event.currentTarget.nextElementSibling;

                                    if (fallback) {
                                      fallback.style.display = "flex";
                                    }
                                  }}
                                />
                              ) : null}

                              <div
                                style={{ display: imageUrl ? "none" : "flex" }}
                                className="absolute inset-0 items-center justify-center bg-blue-50 text-blue-500"
                              >
                                <Boxes size={16} />
                              </div>
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="truncate text-sm font-semibold text-slate-900 sm:text-[15px]">
                                  {pkg?.package_name || "Unnamed package"}
                                </h3>

                                <span className="rounded-md border border-blue-100 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                                  {getPackageTypeLabel(pkg?.package_type)}
                                </span>

                                {childCount > 0 ? (
                                  <span className="rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                                    {childCount} children
                                  </span>
                                ) : null}
                              </div>

                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
                                <span>#{pkg?.package_id}</span>

                                <span>{pkg?.pricing_method || "—"}</span>

                                <span>
                                  {pkg?.base_price
                                    ? formatPrice(pkg.base_price)
                                    : "Variable price"}
                                </span>

                                <span>
                                  Min {pkg?.minimum_persons || "—"} person
                                  {String(pkg?.minimum_persons) === "1"
                                    ? ""
                                    : "s"}
                                </span>
                              </div>
                            </div>
                          </button>

                          <div className="flex shrink-0 items-center gap-2">
                            <span
                              className={`hidden rounded-full px-2 py-1 text-[10px] font-semibold sm:inline-flex ${
                                isActive
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-rose-50 text-rose-700"
                              }`}
                            >
                              {pkg?.status || "-"}
                            </span>

                            <button
                              type="button"
                              onClick={() => openPackageEdit(pkg)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                              title="Edit package"
                            >
                              <Edit3 size={14} />
                            </button>

                            <button
                              type="button"
                              onClick={() => togglePackage(packageId)}
                              className={`flex h-8 w-8 items-center justify-center rounded-full transition ${
                                isExpanded
                                  ? "bg-blue-500 text-white"
                                  : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                              }`}
                              title={
                                isExpanded
                                  ? "Hide package details"
                                  : "View package details"
                              }
                            >
                              {isExpanded ? (
                                <EyeOff size={14} />
                              ) : (
                                <Eye size={14} />
                              )}
                            </button>

                            <ChevronDown
                              size={16}
                              className={`hidden text-slate-400 transition-transform sm:block ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </div>

                        {isExpanded ? (
                          <div className="border-t border-slate-200 bg-slate-50/60 p-3 sm:p-4">
                            <div className="min-w-0">
                              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 xl:grid-cols-8">
                                <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                                    Package ID
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
                                    #{pkg?.package_id || "—"}
                                  </p>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                                    Catalogue
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
                                    #{pkg?.catalogue_id || "—"}
                                  </p>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                                    Pricing
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
                                    {pkg?.pricing_method || "—"}
                                  </p>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                                    Base Price
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
                                    {formatPrice(pkg?.base_price)}
                                  </p>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                                    Minimum
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
                                    {pkg?.minimum_persons || "—"}
                                  </p>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                                    Maximum
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
                                    {pkg?.maximum_persons || "No limit"}
                                  </p>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                                    Increment
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
                                    {pkg?.person_increment || "—"}
                                  </p>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                                    Order
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
                                    {pkg?.display_order || "0"}
                                  </p>
                                </div>
                              </div>

                              <div className="mt-2 flex flex-col gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 sm:flex-row sm:items-center sm:justify-between">
                                <p className="min-w-0 text-[10px] leading-4 text-slate-500">
                                  <span className="font-semibold text-slate-700">
                                    Description:
                                  </span>{" "}
                                  {pkg?.description || "No description added."}
                                </p>

                                <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                                  <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[9px] font-semibold text-blue-700">
                                    {getPackageTypeLabel(pkg?.package_type)}
                                  </span>

                                  <span
                                    className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                                      isActive
                                        ? "bg-emerald-50 text-emerald-700"
                                        : "bg-rose-50 text-rose-700"
                                    }`}
                                  >
                                    {pkg?.status || "-"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <PackageVariantsPanel
                              packageId={pkg?.package_id}
                              packageName={pkg?.package_name}
                              variantsByPackage={bhandarePackageVariants}
                              loadingByPackage={bhandarePackageVariantsLoading}
                              saving={bhandarePackageVariantSaving}
                              deletingId={bhandarePackageVariantDeletingId}
                              fetchVariants={fetchBhandareWalaPackageVariants}
                              insertVariant={insertBhandareWalaPackageVariant}
                              updateVariant={updateBhandareWalaPackageVariant}
                              deleteVariant={deleteBhandareWalaPackageVariant}
                            />

                            {childCount > 0 ? (
                              <div className="mt-3 rounded-xl border border-violet-100 bg-white p-2.5">
                                <div className="mb-2 flex items-center justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="truncate text-[11px] font-semibold text-slate-800">
                                        Combos
                                      </p>

                                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-violet-50 px-1.5 text-[9px] font-bold text-violet-700">
                                        {childCount}
                                      </span>
                                    </div>

                                    <p className="mt-0.5 truncate text-[9px] text-slate-400">
                                      Click any combo to view full details
                                    </p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                                  {(pkg?.children || []).map((child) => {
                                    const childId = child?.package_id;
                                    const childImageUrl = String(
                                      child?.img_url || "",
                                    ).trim();
                                    const childActive =
                                      String(
                                        child?.status || "",
                                      ).toUpperCase() === "ACTIVE";

                                    return (
                                      <button
                                        key={childId}
                                        type="button"
                                        onClick={() =>
                                          setSelectedComboDetails({
                                            child,
                                            parent: pkg,
                                          })
                                        }
                                        title="View combo details"
                                        className="group min-w-0 rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-left transition hover:-translate-y-0.5 hover:border-violet-200 hover:bg-white hover:shadow-sm"
                                      >
                                        <div className="flex items-center gap-2">
                                          <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
                                            {childImageUrl ? (
                                              <img
                                                src={childImageUrl}
                                                alt={
                                                  child?.package_name || "Combo"
                                                }
                                                loading="lazy"
                                                decoding="async"
                                                className="h-full w-full object-cover"
                                                onError={(event) => {
                                                  event.currentTarget.style.display =
                                                    "none";

                                                  const fallback =
                                                    event.currentTarget
                                                      .nextElementSibling;

                                                  if (fallback) {
                                                    fallback.style.display =
                                                      "flex";
                                                  }
                                                }}
                                              />
                                            ) : null}

                                            <div
                                              style={{
                                                display: childImageUrl
                                                  ? "none"
                                                  : "flex",
                                              }}
                                              className="absolute inset-0 items-center justify-center bg-violet-50 text-violet-500"
                                            >
                                              <Boxes size={14} />
                                            </div>
                                          </div>

                                          <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1">
                                              <p
                                                className="truncate text-[10px] font-semibold text-slate-800"
                                                title={
                                                  child?.package_name ||
                                                  "Unnamed combo"
                                                }
                                              >
                                                {child?.package_name ||
                                                  "Unnamed combo"}
                                              </p>

                                              <span
                                                className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                                                  childActive
                                                    ? "bg-emerald-500"
                                                    : "bg-rose-500"
                                                }`}
                                                title={child?.status || "-"}
                                              />
                                            </div>

                                            <p className="mt-0.5 truncate text-[8px] text-slate-400">
                                              #{childId} ·{" "}
                                              {getPackageTypeLabel(
                                                child?.package_type,
                                              )}
                                            </p>

                                            <div className="mt-1 flex items-center justify-between gap-1">
                                              <span className="truncate text-[9px] font-bold text-slate-700">
                                                {formatPrice(child?.base_price)}
                                              </span>

                                              <span className="shrink-0 text-[8px] font-medium text-slate-400">
                                                Min{" "}
                                                {child?.minimum_persons || "—"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </article>
                    );
                  })
                )}
              </>
            )}
          </div>
        </section>
      </main>

      {selectedComboDetails ? (
        <div className="fixed inset-0 z-[11000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px] sm:p-5">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate text-base font-semibold text-slate-900">
                    {selectedComboDetails?.child?.package_name ||
                      "Combo Details"}
                  </h3>

                  <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[9px] font-semibold text-violet-700">
                    {getPackageTypeLabel(
                      selectedComboDetails?.child?.package_type,
                    )}
                  </span>

                  <span
                    className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                      String(
                        selectedComboDetails?.child?.status || "",
                      ).toUpperCase() === "ACTIVE"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-rose-50 text-rose-700"
                    }`}
                  >
                    {selectedComboDetails?.child?.status || "-"}
                  </span>
                </div>

                <p className="mt-0.5 text-[10px] text-slate-400">
                  Parent: {selectedComboDetails?.parent?.package_name || "—"}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedComboDetails(null)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100"
                title="Close"
                aria-label="Close combo details"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 sm:p-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[150px_minmax(0,1fr)]">
                <div className="relative h-[140px] overflow-hidden rounded-xl border border-slate-200 bg-slate-50 md:h-[150px]">
                  {String(selectedComboDetails?.child?.img_url || "").trim() ? (
                    <img
                      src={String(
                        selectedComboDetails?.child?.img_url || "",
                      ).trim()}
                      alt={selectedComboDetails?.child?.package_name || "Combo"}
                      className="h-full w-full object-cover"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                        const fallback = event.currentTarget.nextElementSibling;

                        if (fallback) {
                          fallback.style.display = "flex";
                        }
                      }}
                    />
                  ) : null}

                  <div
                    style={{
                      display: String(
                        selectedComboDetails?.child?.img_url || "",
                      ).trim()
                        ? "none"
                        : "flex",
                    }}
                    className="absolute inset-0 items-center justify-center text-violet-400"
                  >
                    <Boxes size={28} />
                  </div>
                </div>

                <div className="min-w-0">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Package ID
                      </p>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-700">
                        #{selectedComboDetails?.child?.package_id || "—"}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Parent ID
                      </p>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-700">
                        #
                        {selectedComboDetails?.child?.parent_package_id ||
                          selectedComboDetails?.parent?.package_id ||
                          "—"}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Catalogue
                      </p>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-700">
                        #{selectedComboDetails?.child?.catalogue_id || "—"}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Pricing
                      </p>
                      <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-700">
                        {selectedComboDetails?.child?.pricing_method || "—"}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Base Price
                      </p>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-700">
                        {formatPrice(selectedComboDetails?.child?.base_price)}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Minimum
                      </p>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-700">
                        {selectedComboDetails?.child?.minimum_persons || "—"}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Maximum
                      </p>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-700">
                        {selectedComboDetails?.child?.maximum_persons ||
                          "No limit"}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Increment
                      </p>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-700">
                        {selectedComboDetails?.child?.person_increment || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[8px] uppercase tracking-wide text-slate-400">
                        Description
                      </p>

                      <span className="text-[9px] font-semibold text-slate-400">
                        Order{" "}
                        {selectedComboDetails?.child?.display_order || "0"}
                      </span>
                    </div>

                    <p className="mt-1 text-[10px] leading-4 text-slate-600">
                      {selectedComboDetails?.child?.description ||
                        "No description added."}
                    </p>
                  </div>
                </div>
              </div>

              <PackageVariantsPanel
                packageId={selectedComboDetails?.child?.package_id}
                packageName={selectedComboDetails?.child?.package_name}
                variantsByPackage={bhandarePackageVariants}
                loadingByPackage={bhandarePackageVariantsLoading}
                saving={bhandarePackageVariantSaving}
                deletingId={bhandarePackageVariantDeletingId}
                fetchVariants={fetchBhandareWalaPackageVariants}
                insertVariant={insertBhandareWalaPackageVariant}
                updateVariant={updateBhandareWalaPackageVariant}
                deleteVariant={deleteBhandareWalaPackageVariant}
              />

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    const combo = selectedComboDetails?.child;

                    setSelectedComboDetails(null);

                    if (combo) {
                      openPackageEdit(combo);
                    }
                  }}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[10px] font-semibold text-white transition hover:bg-blue-700"
                >
                  <Edit3 size={12} />
                  Edit Combo
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editingItem ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px] sm:p-5">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editingItem?.mode === "ADD"
                    ? "Add Bhandare Item"
                    : "Edit Bhandare Item"}
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {editingItem?.mode === "ADD"
                    ? "Create new item"
                    : `Item #${form.item_id}`}
                </p>
              </div>
              <button
                type="button"
                onClick={closeEdit}
                disabled={isBusy}
                className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-5 p-4 sm:p-5 md:grid-cols-[220px_minmax(0,1fr)]">
              <div>
                <label className="mb-2 block text-xs font-semibold text-slate-700">
                  Item Image
                </label>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isBusy}
                  className="group relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 transition hover:border-emerald-300 hover:bg-emerald-50/40 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Item preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <Camera size={30} />
                      <span className="text-xs font-semibold">
                        Choose image
                      </span>
                    </div>
                  )}

                  <div className="absolute inset-x-3 bottom-3 flex items-center justify-center gap-1.5 rounded-xl bg-slate-950/75 px-3 py-2 text-[11px] font-semibold text-white opacity-0 backdrop-blur transition group-hover:opacity-100">
                    <UploadCloud size={14} />
                    Change image
                  </div>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Item Name
                  </label>
                  <input
                    name="item_name"
                    value={form.item_name}
                    onChange={handleFieldChange}
                    disabled={isBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Item Type
                  </label>
                  <select
                    name="item_type"
                    value={form.item_type}
                    onChange={handleFieldChange}
                    disabled={isBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                  >
                    <option value="VEG">VEG</option>
                    <option value="OTHER">OTHER</option>
                    <option value="NON_VEG">NON_VEG</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Tax Percentage
                  </label>
                  <div className="relative">
                    <input
                      name="tax_percentage"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.tax_percentage}
                      onChange={handleFieldChange}
                      disabled={isBusy}
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 pr-8 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                      %
                    </span>
                  </div>
                </div>

                {editingItem?.mode === "EDIT" ? (
                  <>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                        Add Price
                      </label>
                      <input
                        name="addon_price"
                        value={form.addon_price}
                        onChange={handleFieldChange}
                        disabled={isBusy}
                        placeholder="Optional"
                        className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                        Less Price
                      </label>
                      <input
                        name="less_price"
                        value={form.less_price}
                        onChange={handleFieldChange}
                        disabled={isBusy}
                        placeholder="Optional"
                        className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                        Upgrade Price
                      </label>
                      <input
                        name="upgrade_price"
                        value={form.upgrade_price}
                        onChange={handleFieldChange}
                        disabled={isBusy}
                        placeholder="Optional"
                        className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                        Status
                      </label>
                      <select
                        name="status"
                        value={form.status}
                        onChange={handleFieldChange}
                        disabled={isBusy}
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                      >
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </select>
                    </div>
                  </>
                ) : null}

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Preparation Time
                  </label>
                  <div className="relative">
                    <input
                      name="preparation_time_minutes"
                      type="number"
                      min="0"
                      step="1"
                      value={form.preparation_time_minutes}
                      onChange={handleFieldChange}
                      disabled={isBusy}
                      placeholder="0"
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 pr-12 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                      min
                    </span>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </label>
                  <textarea
                    name="item_description"
                    value={form.item_description}
                    onChange={handleFieldChange}
                    disabled={isBusy}
                    rows={4}
                    placeholder="Item description"
                    className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50"
                  />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:flex-row sm:justify-end sm:px-5">
              <button
                type="button"
                onClick={closeEdit}
                disabled={isBusy}
                className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isBusy}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 text-xs font-semibold text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isBusy ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : editingItem?.mode === "ADD" ? (
                  <Plus size={14} />
                ) : (
                  <Edit3 size={14} />
                )}

                {bhandareImageUploading
                  ? "Uploading image..."
                  : bhandareItemSaving
                    ? editingItem?.mode === "ADD"
                      ? "Adding item..."
                      : "Saving item..."
                    : editingItem?.mode === "ADD"
                      ? "Add Item"
                      : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editingPackage ? (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px] sm:p-5">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Edit Bhandare Package
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  Package #{packageForm.packageId} · Catalogue #
                  {packageForm.catalogueId}
                </p>
              </div>

              <button
                type="button"
                onClick={closePackageEdit}
                disabled={isPackageBusy}
                className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-5 p-4 sm:p-5 lg:grid-cols-[230px_minmax(0,1fr)]">
              <div>
                <label className="mb-2 block text-xs font-semibold text-slate-700">
                  Package Image
                </label>

                <button
                  type="button"
                  onClick={() => packageFileInputRef.current?.click()}
                  disabled={isPackageBusy}
                  className="group relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 transition hover:border-blue-300 hover:bg-blue-50/40 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {packageImagePreview ? (
                    <img
                      src={packageImagePreview}
                      alt="Package preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <Camera size={30} />
                      <span className="text-xs font-semibold">
                        Choose image
                      </span>
                    </div>
                  )}

                  <div className="absolute inset-x-3 bottom-3 flex items-center justify-center gap-1.5 rounded-xl bg-slate-950/75 px-3 py-2 text-[11px] font-semibold text-white opacity-0 backdrop-blur transition group-hover:opacity-100">
                    <UploadCloud size={14} />
                    Change image
                  </div>
                </button>

                <input
                  ref={packageFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePackageFileChange}
                  className="hidden"
                />

                <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/70 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                    Image flow
                  </p>
                  <p className="mt-1 text-[11px] leading-5 text-slate-600">
                    Upload image → update package → refresh packages.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Package Name
                  </label>
                  <input
                    name="packageName"
                    value={packageForm.packageName}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Package Type
                  </label>
                  <select
                    name="packageType"
                    value={packageForm.packageType}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  >
                    <option value="CONFIGURABLE_PACKAGE">
                      CONFIGURABLE_PACKAGE
                    </option>
                    <option value="FIXED_PACKAGE">FIXED_PACKAGE</option>
                    <option value="THALI">THALI</option>
                    <option value="BULK_PACKAGE">BULK_PACKAGE</option>
                    <option value="COMBO">COMBO</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Pricing Method
                  </label>
                  <select
                    name="pricingMethod"
                    value={packageForm.pricingMethod}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  >
                    <option value="SIZE_TIER">SIZE_TIER</option>
                    <option value="FIXED">FIXED</option>
                    <option value="CALCULATED">CALCULATED</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </label>
                  <select
                    name="status"
                    value={packageForm.status}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Parent Package ID
                  </label>
                  <input
                    name="parentPackageId"
                    value={packageForm.parentPackageId}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    placeholder="Blank for root package"
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Minimum Persons
                  </label>
                  <input
                    name="minimumPersons"
                    type="number"
                    min="0"
                    value={packageForm.minimumPersons}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Maximum Persons
                  </label>
                  <input
                    name="maximumPersons"
                    type="number"
                    min="0"
                    value={packageForm.maximumPersons}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    placeholder="Blank = no limit"
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Person Increment
                  </label>
                  <input
                    name="personIncrement"
                    type="number"
                    min="0"
                    value={packageForm.personIncrement}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Base Price
                  </label>
                  <input
                    name="basePrice"
                    type="number"
                    min="0"
                    step="0.01"
                    value={packageForm.basePrice}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    placeholder="0"
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Display Order
                  </label>
                  <input
                    name="displayOrder"
                    type="number"
                    min="0"
                    value={packageForm.displayOrder}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  />
                </div>

                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={packageForm.description}
                    onChange={handlePackageFieldChange}
                    disabled={isPackageBusy}
                    rows={4}
                    placeholder="Package description"
                    className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:flex-row sm:justify-end sm:px-5">
              <button
                type="button"
                onClick={closePackageEdit}
                disabled={isPackageBusy}
                className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handlePackageSave}
                disabled={isPackageBusy}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-blue-500 px-5 text-xs font-semibold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isPackageBusy ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Edit3 size={14} />
                )}

                {bhandareImageUploading
                  ? "Uploading image..."
                  : bhandarePackageSaving
                    ? "Saving package..."
                    : "Save Package"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}